import { useState } from 'react'
import { createRoute } from '@tanstack/react-router'
import { rootRoute, requireAuth } from './__root'
import { KanbanBoard } from '../components/kanban/KanbanBoard'
import { CardDetailModal } from '../components/kanban/CardDetailModal'
import { useCards, useUpdateCard, useCreateCard } from '../api/hooks'
import type { DashboardCard } from '../api/client'

export const legacyKanbanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/_legacy/kanban',
  beforeLoad: requireAuth,
  component: KanbanPage,
})

function KanbanPage() {
  const { data, isLoading, error } = useCards()
  const updateCard = useUpdateCard()
  const createCard = useCreateCard()
  const [selectedCard, setSelectedCard] = useState<DashboardCard | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newTitle, setNewTitle] = useState('')

  const handleStatusChange = (cardId: string, newStatus: string) => {
    updateCard.mutate({ id: cardId, status: newStatus })
  }

  const handleCardUpdate = (fields: Partial<Pick<DashboardCard, 'description' | 'priority' | 'title'>>) => {
    if (selectedCard) {
      updateCard.mutate({ id: selectedCard.id, ...fields })
      setSelectedCard({ ...selectedCard, ...fields })
    }
  }

  const handleCreate = () => {
    if (!newTitle.trim()) return
    createCard.mutate({ title: newTitle.trim() }, {
      onSuccess: () => {
        setNewTitle('')
        setShowCreateForm(false)
      },
    })
  }

  if (isLoading) {
    return (
      <div data-testid="kanban-board" className="p-6">
        <h1 className="text-2xl font-bold mb-4">Board</h1>
        <p className="text-gray-400">Loading cards...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div data-testid="kanban-board" className="p-6">
        <h1 className="text-2xl font-bold mb-4">Board</h1>
        <p className="text-red-400">Failed to load cards: {error.message}</p>
      </div>
    )
  }

  const cards = data?.cards ?? []

  return (
    <div data-testid="kanban-board" className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Board</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
        >
          + New Card
        </button>
      </div>

      {showCreateForm && (
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            placeholder="Card title..."
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
            autoFocus
          />
          <button
            onClick={handleCreate}
            disabled={!newTitle.trim() || createCard.isPending}
            className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white rounded transition-colors"
          >
            Create
          </button>
          <button
            onClick={() => { setShowCreateForm(false); setNewTitle('') }}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg mb-2">No cards yet</p>
          <p className="text-sm">Create a card to get started, or use chat to generate tasks.</p>
        </div>
      ) : (
        <KanbanBoard
          cards={cards}
          onCardClick={setSelectedCard}
          onStatusChange={handleStatusChange}
        />
      )}

      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          open={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          onUpdate={handleCardUpdate}
          onStatusChange={(newStatus) => handleStatusChange(selectedCard.id, newStatus)}
        />
      )}
    </div>
  )
}
